import { defaultTo } from 'aa_common/front-end/helpers/common'
import { useNextEffect } from 'common/hooks/use-next-effect'
import { FORM_MODES } from 'common/models'
import { PresenterPreFillDraftAssetResponse } from 'common/open-api'
import { useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { getExciseSettingsByDateList } from 'store/settings/excise/selectors'

import { FORM_FIELDS } from '../const'
import { getExciseInputSetting } from '../helper'
import { setAcquisitionCost } from '../helpers/set-acquisition-cost'

/**
 * Custom hook to set form values based on a calculated draft fixed asset response.
 * This hook should be the last hook called in the component to ensure that it runs after all other hooks have been executed.
 * @param formMode - Use in correction mode to prevent re-assign data to the form in the first time
 * @param calculatedDraftFA - The calculated draft fixed asset response.
 */

export const useSetFromCalculatedDraft = (
  formMode: FORM_MODES,
  calculatedDraftFA?: PresenterPreFillDraftAssetResponse
) => {
  const { setFieldValue, getFieldProps } = useFormikContext()
  const { data: exciseSetting } = useSelector(getExciseSettingsByDateList, isEqual)
  const firstTimeCorrectionModeRef = useRef<boolean>(formMode === FORM_MODES.CORRECTION)

  useNextEffect(() => {
    const { isExciseSettingAvailable, isInputTaxWhenRegister, isTaxMethodExcluded, isInternalTax, isShowTax } =
      getExciseInputSetting(exciseSetting)

    if (!isExciseSettingAvailable || !calculatedDraftFA) {
      return
    }

    if (firstTimeCorrectionModeRef.current) {
      firstTimeCorrectionModeRef.current = false
      return
    }

    const acquisitionCostExcluded = defaultTo(calculatedDraftFA?.acquisition_cost_excluded_excise, 0)
    const exciseValue = defaultTo(calculatedDraftFA?.excise_value, 0)
    let acquisitionCostInput = acquisitionCostExcluded
    if (isInputTaxWhenRegister && (!isTaxMethodExcluded || isInternalTax)) {
      acquisitionCostInput = acquisitionCostExcluded + exciseValue
    }

    if (acquisitionCostInput < 0) {
      acquisitionCostInput = 0
    }

    setFieldValue(FORM_FIELDS.SET_DATA_FROM_PREFILL, true)

    setFieldValue('acquisition_cost_input', acquisitionCostInput)
    setAcquisitionCost(
      acquisitionCostInput,
      exciseValue,
      { isInputTaxWhenRegister, isTaxMethodExcluded, isInternalTax },
      setFieldValue
    )
    if (isInputTaxWhenRegister) {
      setFieldValue('excise_id', calculatedDraftFA?.excise_id)
    }
    if (isShowTax) {
      setFieldValue('manual_excise_value', exciseValue)
      setFieldValue('calculated_excise_value', exciseValue)
    }
    if (!getFieldProps('urls')?.value && calculatedDraftFA?.urls?.length) {
      setFieldValue('urls', calculatedDraftFA?.urls)
    }
    if (!getFieldProps('memo')?.value) {
      setFieldValue('memo', calculatedDraftFA?.memo)
    }

    if (formMode !== FORM_MODES.EDIT) {
      if (calculatedDraftFA?.department_biid) {
        setFieldValue('department_biid', {
          value: calculatedDraftFA?.department_biid,
          label: calculatedDraftFA?.department_name ?? '',
        })
      }
    }
  }, [exciseSetting, calculatedDraftFA, formMode])
}
